import React, { MouseEvent } from 'react';
import cn from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import IDeal from '@/finsight/domain/vo/IDeal';
import Tools from './Tools/Tools';
import Dates from './Dates/Dates';
import Leads from './Leads/Leads';
import Region from './Region/Region';
import DealStatus from '@/finsight/ui/common/components/TableCells/DealStatus';
import ParentCompany from './ParentCompany/ParentCompany';
import TickerDealName from './TickerDealName/TickerDealName';
import SectorIndustry from './SectorIndustry/SectorIndustry';
import ProductAbbreviation from './ProductAbbreviation/ProductAbbreviation';
import { mapDealFields } from '@/finsight/ui/components/deals/DealsList/DealHeader/helpers';
import UpsizedDeal from '@/finsight/ui/components/deals/DealsList/DealHeader/UpsizedDeal/UpsizedDeal';
import { useDealTooltipContext } from '@/finsight/application/Deal/DealTooltipContext';
import { RestrictedAccessExplanation } from '@/finsight/ui/common/components/Explanations';
import { UserPlanType } from '@/users/domain/UserPlan';
import CheckUserPlanAccess from '@/finsight/ui/common/components/CheckUserPlanAccess';
import { usePopoverModifier } from '@/finsight/ui/common/components/Explanations/RestrictedAccessExplanation/usePopoverModifier';
import { PRICING_EXPLAINATION_TITLE, PRICING_EXPLAINATION_DESCRIPTION } from '@/finsight/ui/components/deals/constants';

import styles from './dealHeader.scss';

interface IDealHeaderProps {
  isFirst: boolean,
  isLast: boolean,
  isExpanded: boolean,
  deal: IDeal,
  onClick?: (deal: string) => void,
}

const DealHeaderPremiumization = (props: IDealHeaderProps) => {
  const { dealTooltipOpened, setDealTooltipOpened } = useDealTooltipContext();
  const deal = mapDealFields(cloneDeep(props.deal));
  const { isExpanded, isFirst } = props;
  const { popperModifiers, setTooltipOffset, getTooltipOffset } = usePopoverModifier();

  const onHeaderClick = (event: MouseEvent) => {
    const newOffset = getTooltipOffset(event);

    event.stopPropagation();
    props.onClick?.(deal.id);
    setTooltipOffset(newOffset);
  };

  const dealHeaderContent = () => (
    <div
      className={ cn(styles.header, { [styles.isFirst]: isFirst, [styles.isCollapsed]: !isExpanded }) }
      onClick={ onHeaderClick }
    >
      <ProductAbbreviation deal={ deal } />
      <SectorIndustry deal={ deal } />
      <UpsizedDeal deal={ deal } />
      <DealStatus status={ deal.status } />
      <TickerDealName deal={ deal } />
      <ParentCompany deal={ deal } />
      <Leads deal={ deal } isLast={ props.isLast } />
      <Region deal={ deal } />
      <Dates deal={ deal } />
      <Tools
        deal={ deal }
        isExpanded={ props.isExpanded }
      />
    </div>
  );

  return deal && deal.id && (
  <CheckUserPlanAccess
    requiredUserPlan={ UserPlanType.PREMIUM }
    fallback={ (
      <RestrictedAccessExplanation
        title={ PRICING_EXPLAINATION_TITLE }
        description={ PRICING_EXPLAINATION_DESCRIPTION }
        isOpen={ dealTooltipOpened === deal.id }
        closeTooltip={ () => setDealTooltipOpened(null) }
        popperModifiers={ popperModifiers }
      >
        { dealHeaderContent() }
      </RestrictedAccessExplanation>
    ) }
  >
    { dealHeaderContent() }
  </CheckUserPlanAccess>
  );
};

export default DealHeaderPremiumization;
